/**
 * printReceipt(receipt)
 *
 * Opens a new browser window with a print-ready payment receipt and
 * auto-triggers window.print() so the user can save as PDF.
 *
 * receipt shape:
 *   receiptNumber   string   e.g. "REC-20260507-001"
 *   firm            { name, email, phone, address }
 *   client          { first_name, last_name, organization, email, phone }
 *   matterTitle     string
 *   amount          number
 *   payment_date    string  ISO date
 *   method          string  e.g. "cash" | "bank_transfer" | "cheque" | "mobile_money" | "card" | "other"
 *   payment_label   string  (optional)
 *   notes           string  (optional)
 *   linkedCharges   [{ description, amount }]   (optional)
 *   linkedInvoices  [{ invoice_number, total }]  (optional)
 */

// Small helper: escape user-supplied text before injecting into the HTML.
const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export function printReceipt(receipt) {
  const {
    receiptNumber = 'REC-UNKNOWN',
    firm = {},
    client = {},
    matterTitle = '',
    amount = 0,
    payment_date,
    method = '',
    payment_label = '',
    notes = '',
    linkedCharges = [],
    linkedInvoices = [],
    issuer_name,                              // snapshot of firm.name at issue time (migration 085)
  } = receipt;
  const displayFirmName = issuer_name || firm.name || 'Law Firm';

  const clientName = [client.first_name, client.last_name].filter(Boolean).join(' ')
    || client.organization || '—';

  const fmt = (n) =>
    'GH₵' + Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const methodLabel = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    mobile_money: 'Mobile Money',
    card: 'Card',
    other: 'Other',
  }[method] || method;

  const appliedRows = [
    ...linkedInvoices.map((inv) => `
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid #f1f5f9;color:#334155;">Invoice ${escapeHtml(inv.invoice_number || '')}</td>
        <td style="padding:8px 0;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:600;color:#1e293b;">${fmt(inv.total)}</td>
      </tr>`),
    ...linkedCharges.map((c) => `
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid #f1f5f9;color:#334155;">${escapeHtml(c.description || 'Charge')}</td>
        <td style="padding:8px 0;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:600;color:#1e293b;">${fmt(c.amount)}</td>
      </tr>`),
  ].join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Receipt ${escapeHtml(receiptNumber)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 13px; color: #1e293b; background: #fff; }
    @page { size: A5; margin: 12mm 14mm; }
    @media print { .no-print { display: none !important; } }

    .page { max-width: 560px; margin: 0 auto; padding: 32px; }

    /* Header */
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2px solid #4f46e5; gap: 16px; }
    .firm-block { display: flex; gap: 12px; align-items: center; }
    .firm-logo { max-height: 48px; max-width: 140px; width: auto; height: auto; object-fit: contain; }
    .firm-name { font-size: 20px; font-weight: 700; color: #1e293b; }
    .firm-meta { color: #64748b; font-size: 11px; margin-top: 5px; line-height: 1.7; }
    .rec-label { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #94a3b8; text-align: right; }
    .rec-number { font-size: 16px; font-weight: 700; text-align: right; margin-top: 2px; color: #1e293b; }
    .rec-badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 10px; font-weight: 700;
      color: #fff; background: #10b981; margin-top: 6px; letter-spacing: 0.5px; }

    /* Amount hero */
    .amount-hero { text-align: center; background: linear-gradient(135deg, #4f46e5, #7c3aed); border-radius: 12px;
      padding: 24px 20px; margin-bottom: 24px; color: #fff; }
    .amount-label { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.8; margin-bottom: 6px; }
    .amount-value { font-size: 32px; font-weight: 800; letter-spacing: -1px; }

    /* Details grid */
    .details { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
    .detail-cell { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
    .detail-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 4px; }
    .detail-value { font-size: 13px; font-weight: 600; color: #1e293b; }

    /* Applied to */
    .section-title { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }

    /* Notes */
    .notes { padding: 12px 14px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 20px; }
    .notes-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 5px; }
    .notes-text { color: #475569; line-height: 1.6; white-space: pre-wrap; font-size: 12px; }

    /* Footer */
    .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e2e8f0;
      text-align: center; font-size: 11px; color: #94a3b8; line-height: 1.7; }

    /* Print button */
    .no-print {
      position: fixed; top: 16px; right: 16px; z-index: 999;
      background: #4f46e5; color: #fff; border: none; border-radius: 8px;
      padding: 10px 20px; font-size: 14px; font-weight: 600;
      cursor: pointer; display: flex; align-items: center; gap: 8px;
      box-shadow: 0 4px 12px rgba(79,70,229,0.4);
    }
    .no-print:hover { background: #4338ca; }
  </style>
</head>
<body>
<button class="no-print" onclick="window.print()">⬇ Download PDF</button>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div class="firm-block">
      ${firm.logo_url
        ? `<img class="firm-logo" src="${escapeHtml(firm.logo_url)}" alt="${escapeHtml(firm.name || 'Firm logo')}" crossorigin="anonymous" />`
        : ''}
      <div>
        <div class="firm-name">${escapeHtml(displayFirmName)}</div>
        <div class="firm-meta">
          ${[firm.address, firm.email, firm.phone].filter(Boolean).map(escapeHtml).join('<br>')}
        </div>
      </div>
    </div>
    <div style="text-align:right;">
      <div class="rec-label">Payment Receipt</div>
      <div class="rec-number">${escapeHtml(receiptNumber)}</div>
      <div><span class="rec-badge">PAID</span></div>
    </div>
  </div>

  <!-- Amount hero -->
  <div class="amount-hero">
    <div class="amount-label">Amount Received</div>
    <div class="amount-value">${fmt(amount)}</div>
  </div>

  <!-- Details -->
  <div class="details">
    <div class="detail-cell">
      <div class="detail-label">Received From</div>
      <div class="detail-value">${escapeHtml(clientName)}</div>
      ${client.organization && clientName !== client.organization ? `<div style="color:#64748b;font-size:11px;margin-top:2px;">${escapeHtml(client.organization)}</div>` : ''}
    </div>
    <div class="detail-cell">
      <div class="detail-label">Payment Date</div>
      <div class="detail-value">${escapeHtml(fmtDate(payment_date))}</div>
    </div>
    <div class="detail-cell">
      <div class="detail-label">Payment Method</div>
      <div class="detail-value">${escapeHtml(methodLabel)}</div>
    </div>
    <div class="detail-cell">
      <div class="detail-label">Matter</div>
      <div class="detail-value" style="font-size:12px;">${escapeHtml(matterTitle || '—')}</div>
    </div>
    ${payment_label ? `
    <div class="detail-cell" style="grid-column:span 2;">
      <div class="detail-label">Reference</div>
      <div class="detail-value">${escapeHtml(payment_label)}</div>
    </div>` : ''}
  </div>

  ${appliedRows ? `
  <!-- Applied to -->
  <div class="section-title">Applied to</div>
  <table>
    <tbody>${appliedRows}</tbody>
  </table>` : ''}

  ${notes ? `
  <div class="notes">
    <div class="notes-label">Notes</div>
    <div class="notes-text">${escapeHtml(notes)}</div>
  </div>` : ''}

  <div class="footer">
    This is an official payment receipt issued by ${escapeHtml(displayFirmName)}.<br>
    ${firm.email ? `For enquiries, contact ${escapeHtml(firm.email)}` : ''}
  </div>
</div>
<script>
  window.addEventListener('load', () => setTimeout(() => window.print(), 600));
</script>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}

/**
 * Generate a receipt number: REC-YYYYMMDD-NNN
 * Accepts an array of existing payment objects (with any field that might hold a receipt number).
 */
export function nextReceiptNumber(allPayments = []) {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const max = allPayments.reduce((best, p) => {
    const m = (p.receipt_number || '').match(/(\d+)$/);
    const n = m ? parseInt(m[1], 10) : 0;
    return n > best ? n : best;
  }, 0);
  return `REC-${stamp}-${String(max + 1).padStart(3, '0')}`;
}
